/**
 * Timeline utility functions for trip timeline clustering and distance calculations
 */

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in meters
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000; // Earth radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in meters
}

/**
 * Cluster timeline points that are within 15 meters of each other
 * @param {Array} points - Array of timeline snapshot points
 * @returns {Array} Array of clustered and individual points
 */
export function clusterTimelinePoints(points) {
    if (!points || points.length === 0) {
        return [];
    }

    const clustered = [];
    const processed = new Set();

    for (let i = 0; i < points.length; i++) {
        if (processed.has(i)) continue;

        const currentPoint = points[i];
        const cluster = [currentPoint];
        processed.add(i);

        // Look ahead for nearby points
        for (let j = i + 1; j < points.length; j++) {
            if (processed.has(j)) continue;

            const nextPoint = points[j];
            const distance = haversineDistance(
                currentPoint.location[0], currentPoint.location[1],
                nextPoint.location[0], nextPoint.location[1]
            );

            // If within 15 meters, add to cluster
            if (distance <= 15) {
                cluster.push(nextPoint);
                processed.add(j);
            } else {
                break; // Stop looking ahead if too far
            }
        }

        // Create cluster if 2+ points
        if (cluster.length >= 2) {
            const firstPoint = cluster[0];
            const lastPoint = cluster[cluster.length - 1];

            // Calculate average location
            const avgLat = cluster.reduce((sum, p) => sum + p.location[0], 0) / cluster.length;
            const avgLng = cluster.reduce((sum, p) => sum + p.location[1], 0) / cluster.length;

            clustered.push({
                type: 'cluster',
                id: `cluster-${firstPoint._id}`,
                location: [avgLat, avgLng],
                startTime: firstPoint.locationTimestamp,
                endTime: lastPoint.locationTimestamp,
                startBattery: firstPoint.battery,
                endBattery: lastPoint.battery,
                distanceSoFar: lastPoint.distanceSoFar,
                points: cluster
            });
        } else {
            // Single point
            clustered.push({
                type: 'individual',
                point: currentPoint
            });
        }
    }

    return clustered;
}

/**
 * Format duration between two timestamps in a human-readable format
 * @param {number} startTime - Start timestamp in milliseconds
 * @param {number} endTime - End timestamp in milliseconds
 * @returns {string} Formatted duration
 */
export function formatDuration(startTime, endTime) {
    const diffMs = endTime - startTime;
    const diffMinutes = Math.floor(diffMs / 1000 / 60);
    
    if (diffMinutes < 60) {
        return `${diffMinutes}m`;
    } else {
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        return `${hours}h ${minutes}m`;
    }
}

/**
 * Format time in 12-hour format
 * @param {number} timestamp - Timestamp in milliseconds
 * @returns {string} Formatted time (e.g., "2:45 PM")
 */
export function formatTime(timestamp) {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
    });
}

/**
 * Get battery color based on battery percentage
 * @param {number} battery - Battery percentage (0-100)
 * @returns {string} Background color for battery indicator
 */
export function getBatteryColor(battery) {
    if (battery > 20) {
        return '#E8F5E9'; // Light green
    } else if (battery >= 10) {
        return '#FFF3E0'; // Light orange
    } else {
        return '#FFEBEE'; // Light red
    }
}

/**
 * Get battery text color based on battery percentage
 * @param {number} battery - Battery percentage (0-100)
 * @returns {string} Text color for battery indicator
 */
export function getBatteryTextColor(battery) {
    if (battery > 20) {
        return '#2E7D32'; // Dark green
    } else if (battery >= 10) {
        return '#F57C00'; // Dark orange
    } else {
        return '#C62828'; // Dark red
    }
}
